import Service, { inject as service } from '@ember/service';
import { action } from '@ember/object';
import ApiService from './api';
import AccountService from './account';
import { tracked } from '@glimmer/tracking';
import EnvironmentService from 'cio/services/environment';
import { task } from 'ember-concurrency';
import { SettingsEndpointResponseSettings } from 'cio/models/environment';
import { DEFAULT_MAX_COLLECTION_COUNT } from 'cio/utils/collections';

type SettingsMap = { [key: string]: string[] };
type InitialValue = string | string[] | number | SettingsMap;

type EnvironmentSetting =
  | 'customerColumns'
  | 'objectTypeColumns'
  | 'relationshipColumns'
  | 'metricsTimezone'
  | 'journeyMetricsTimezone'
  | 'maxCollectionSize';
type APIEnvironmentSetting =
  | 'customer_columns'
  | 'object_type_columns'
  | 'relationship_columns'
  | 'metrics_timezone'
  | 'journey_metrics_timezone'
  | 'max_collection_size';

type EnvironmentSettings = {
  customerColumns: string[];
  objectTypeColumns: SettingsMap;
  relationshipColumns: SettingsMap;
  metricsTimezone: string;
  journeyMetricsTimezone: string;
  maxCollectionSize: number;
};

const ENVIRONMENT_SETTINGS: Record<
  EnvironmentSetting,
  {
    apiKey: APIEnvironmentSetting;
    initialValue: InitialValue;
  }
> = {
  customerColumns: {
    apiKey: 'customer_columns',
    initialValue: ['id'],
  },
  objectTypeColumns: {
    apiKey: 'object_type_columns',
    initialValue: {},
  },
  relationshipColumns: {
    apiKey: 'relationship_columns',
    initialValue: {},
  },
  metricsTimezone: {
    apiKey: 'metrics_timezone',
    initialValue: 'Etc/GMT+4',
  },
  journeyMetricsTimezone: {
    apiKey: 'journey_metrics_timezone',
    initialValue: 'Etc/GMT+4',
  },
  maxCollectionSize: {
    apiKey: 'max_collection_size',
    initialValue: DEFAULT_MAX_COLLECTION_COUNT,
  },
};

const DEFAULT_VALUES = {
  customerColumns: [...(ENVIRONMENT_SETTINGS.customerColumns.initialValue as string[])],
  objectTypeColumns: { ...(ENVIRONMENT_SETTINGS.objectTypeColumns.initialValue as SettingsMap) },
  relationshipColumns: { ...(ENVIRONMENT_SETTINGS.relationshipColumns.initialValue as SettingsMap) },
  metricsTimezone: ENVIRONMENT_SETTINGS.metricsTimezone.initialValue as string,
  journeyMetricsTimezone: ENVIRONMENT_SETTINGS.journeyMetricsTimezone.initialValue as string,
  maxCollectionSize: ENVIRONMENT_SETTINGS.maxCollectionSize.initialValue as number,
};

// The backend currently uses "Eastern" as the name for a UTC -04:00 timezone,
// but that is not recognized as an official IANA timezone name, so we need to
// map it.
// In future, when we allow changing timezones for metrics, we'll use a more
// robust list of IANA timezones for customers to choose from, so there won't be
// any extra exceptions to this.
function normalizeTimezone(timezone: string) {
  if (timezone === 'Eastern') {
    return 'Etc/GMT+4';
  }

  return timezone;
}

export default class SettingsService extends Service {
  @service api!: ApiService;
  @service account!: AccountService;
  @service environment!: EnvironmentService;

  @tracked environmentSettings: EnvironmentSettings = { ...DEFAULT_VALUES };

  loadEnvironmentSettings = task(async (clearSettings = false) => {
    if (clearSettings) {
      this.resetEnvironmentSettings();
    }

    let apiSettings = await this.environment.current.getSettings({});

    this.setEnvironmentSettings(apiSettings.settings);
  });

  saveEnvironmentSetting = task(async (key: EnvironmentSetting, value: string[] | SettingsMap) => {
    let updatedApiSettings = await this.environment.current.updateSetting({
      settings: {
        [ENVIRONMENT_SETTINGS[key].apiKey]: value,
      },
    });

    this.setEnvironmentSettings(updatedApiSettings.settings);
  });

  // used to remove attribute from settings when there is no customer/objectType/relationship with an attribute that is saved in settings
  // it compares attributes in settings vs attributes for sentAttributes/sentRelAttributes for customer/objectType/relationship
  cleanupAttributesFromSettings(setting: EnvironmentSetting, attributes: string[] = [], id?: string) {
    let updatedColumns = [];
    let currentColumns =
      id != null
        ? (this.environmentSettings[setting] as Record<string, any>)[id as string] ?? []
        : this.environmentSettings[setting] ?? [];

    for (let attribute of currentColumns) {
      if (attributes?.includes(attribute) === true) {
        updatedColumns.push(attribute);
      }
    }

    if (currentColumns.length !== updatedColumns.length) {
      if (id) {
        this.saveEnvironmentSetting.perform(setting, { [id]: updatedColumns });
      } else {
        this.saveEnvironmentSetting.perform(setting, updatedColumns);
      }
    }
  }

  @action
  private resetEnvironmentSettings() {
    this.environmentSettings = { ...DEFAULT_VALUES };
  }

  @action
  private setEnvironmentSettings(payload: Partial<SettingsEndpointResponseSettings>) {
    let environmentSettings = Object.entries(ENVIRONMENT_SETTINGS).reduce<
      Partial<Record<EnvironmentSetting, InitialValue>>
    >((acc, [key, setting]) => {
      if (payload[setting.apiKey] == null) {
        if (this.environmentSettings[key as EnvironmentSetting]) {
          acc[key as EnvironmentSetting] = this.environmentSettings[key as EnvironmentSetting];
        }
        return acc;
      }
      let value = payload[setting.apiKey];

      if (key === 'metricsTimezone' || key === 'journeyMetricsTimezone') {
        value = normalizeTimezone(value as string);
      }
      acc[key as EnvironmentSetting] = value;
      return acc;
    }, {});
    this.environmentSettings = environmentSettings as EnvironmentSettings;
  }
}

declare module '@ember/service' {
  interface Registry {
    settings: SettingsService;
  }
}
